import { useEffect, useRef, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import IconSvg from 'src/assets/icons/IconSvg';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
const collections = [
  { id: 1, name: 'My Collection' },
  { id: 2, name: 'Favorite Arts' },
  { id: 3, name: 'Photography' },
  { id: 4, name: 'Digital Art' },
  { id: 5, name: 'Illustrations' },
  { id: 6, name: 'Cycling' },
  { id: 7, name: 'Gaming' },
  { id: 8, name: 'Sports' },
  { id: 9, name: 'Music' },
  { id: 10, name: 'Party' },
  { id: 11, name: 'Dance' },
  { id: 12, name: 'Fashion' },
  { id: 13, name: 'Food' },
  { id: 14, name: 'Travel' },
  { id: 15, name: 'Art' },
];

const SearchCollectionInput = ({ className }: { className?: string }) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<(typeof collections)[0] | null>(null);

  const textRef = useRef<HTMLSpanElement>(null);
  const [iconLeft, setIconLeft] = useState(120); // Default left position

  const navigate = useNavigate();

  useEffect(() => {
    if (selected?.name && textRef.current) {
      setIconLeft(textRef.current.offsetWidth);
    }
  }, [selected]);

  const filteredCollections =
    query === ''
      ? collections
      : collections.filter(collection =>
          collection.name.toLowerCase().includes(query.toLowerCase()),
        );

  const handleCollectionSelect = (collection: (typeof collections)[0] | null) => {
    setSelected(collection);

    if (collection) {
      const urlFriendlyName = collection.name.toLowerCase().replace(/\s+/g, '-');
      navigate(`/collection/${urlFriendlyName}`);
    }
  };

  return (
    <Combobox
      value={selected}
      onChange={handleCollectionSelect}
      onClose={() => setQuery('')}
      immediate
    >
      <div className="relative w-full xl:w-[480px]">
        {!selected ? (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-grayscale-400">
            <IconSvg icon="searchM" />
          </div>
        ) : (
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full"></div>
          </div>
        )}

        <span ref={textRef} className="absolute invisible whitespace-pre">
          {selected?.name}
        </span>

        <ComboboxInput
          className={clsx(
            'py-2 w-full h-10 text-base rounded-lg border border-b-0 border-transparent data-[open]:border-brand-granite data-[open]:rounded-b-none bg-grayscale-900',
            'text-grayscale-400 placeholder:text-grayscale-400',
            'px-11 outline-none focus:ring-1 focus:ring-grayscale-700',
            className,
          )}
          displayValue={(item: (typeof collections)[0] | null) => item?.name ?? ''}
          onChange={event => !selected && setQuery(event.target.value)}
          placeholder="Search"
          autoComplete="off"
          readOnly={!!selected}
        />

        {selected && (
          <span
            className="absolute top-1/2 -translate-y-1/2"
            style={{ left: `calc(${iconLeft}px + 50px)` }}
          >
            <IconSvg icon="verifiedS" className="text-brand-tangerine" />
          </span>
        )}

        {selected ? (
          <button
            onClick={e => {
              e.preventDefault();
              setSelected(null);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-grayscale-500 hover:text-grayscale-300"
          >
            <IconSvg icon="closeS" />
          </button>
        ) : (
          <div className="absolute right-3 top-1/2 text-lg font-medium rounded-md -translate-y-1/2 bg-brand-black text-brand-granite px-[9px] w-[26px] h-[26px] flex items-center justify-center">
            /
          </div>
        )}

        <ComboboxOptions
          className={clsx(
            'absolute w-full rounded-b-lg border border-t-0 border-brand-granite',
            'bg-grayscale-900',
            'px-3 py-2',
            'z-10 transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
          )}
        >
          <div className="space-y-3 max-h-[380px] overflow-y-auto custom-scrollbar">
            {filteredCollections.map(collection => (
              <ComboboxOption
                key={collection.id}
                value={collection}
                className={clsx(
                  'flex gap-2 items-center',
                  'text-sm cursor-pointer text-grayscale-400',
                )}
              >
                <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full"></div>

                <span>{collection.name}</span>

                <IconSvg icon="verifiedS" className="text-brand-tangerine" />
              </ComboboxOption>
            ))}

            {filteredCollections.length === 0 && (
              <div className="text-sm text-grayscale-400">No results found</div>
            )}
          </div>
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default SearchCollectionInput;
